'use client';
import { useEffect, useState } from 'react';
import { useRoomContext } from '@livekit/components-react';
import { RoomEvent } from 'livekit-client';
const EMPTY = {
    activeNodeId: null,
    previousNodeId: null,
    lastTransition: null,
    isEnded: false,
    isFinalized: false,
};
/**
 * Subscribes to the worker's `node-graph.switch` and `node-graph.end`
 * data-channel messages and exposes the current node highlight state.
 *
 * The underlying LiveKit Room is stable across a page's lifetime (see
 * WebCallRoom), so this hook is mounted continuously — resetting state
 * when a new call starts is the caller's contract, driven by
 * `sessionKey`.
 */
export function useGraphEvents({ sessionKey, startNodeId, }) {
    const room = useRoomContext();
    const [state, setState] = useState(EMPTY);
    // Reset when the session changes. A new sessionKey clears the previous
    // call's highlight and seeds the new call's start node; a null sessionKey
    // clears everything.
    useEffect(() => {
        if (sessionKey) {
            setState({
                activeNodeId: startNodeId,
                previousNodeId: null,
                lastTransition: null,
                isEnded: false,
                isFinalized: false,
            });
        }
        else {
            setState(EMPTY);
        }
    }, [sessionKey, startNodeId]);
    useEffect(() => {
        const decoder = new TextDecoder();
        const onData = (payload, _participant, _kind, topic) => {
            if (topic !== 'node-graph.switch' &&
                topic !== 'node-graph.end' &&
                topic !== 'node-graph.finalized') {
                return;
            }
            // Finalized carries only a timestamp; nothing to merge into the
            // transition state, just flip the flag.
            if (topic === 'node-graph.finalized') {
                setState(prev => ({ ...prev, isFinalized: true }));
                return;
            }
            let body;
            try {
                body = JSON.parse(decoder.decode(payload));
            }
            catch {
                return;
            }
            if (typeof body.eventType !== 'string')
                return;
            const transition = {
                fromNode: body.fromNode ?? null,
                toNode: body.toNode ?? null,
                eventType: body.eventType,
                turnIndex: body.turnIndex ?? 0,
                timestamp: body.timestamp ?? new Date().toISOString(),
            };
            setState(prev => ({
                activeNodeId: transition.toNode,
                previousNodeId: transition.fromNode ?? prev.activeNodeId,
                lastTransition: transition,
                isEnded: topic === 'node-graph.end' || prev.isEnded,
                isFinalized: prev.isFinalized,
            }));
        };
        room.on(RoomEvent.DataReceived, onData);
        return () => {
            room.off(RoomEvent.DataReceived, onData);
        };
    }, [room]);
    return state;
}
/**
 * Chronological log of graph transitions seen on this room. Same data-channel
 * subscription as {@link useGraphEvents}, but appends instead of overwriting.
 * The list stays capped so a long call cannot grow it without bound.
 */
export function useGraphEventLog(cap = 200) {
    const room = useRoomContext();
    const [entries, setEntries] = useState([]);
    useEffect(() => {
        const decoder = new TextDecoder();
        const onData = (payload, _participant, _kind, topic) => {
            if (topic !== 'node-graph.switch' && topic !== 'node-graph.end')
                return;
            let body;
            try {
                body = JSON.parse(decoder.decode(payload));
            }
            catch {
                return;
            }
            if (typeof body.eventType !== 'string')
                return;
            const transition = {
                fromNode: body.fromNode ?? null,
                toNode: body.toNode ?? null,
                eventType: body.eventType,
                turnIndex: body.turnIndex ?? 0,
                timestamp: body.timestamp ?? new Date().toISOString(),
            };
            setEntries(prev => {
                const next = [...prev, transition];
                return next.length > cap ? next.slice(-cap) : next;
            });
        };
        room.on(RoomEvent.DataReceived, onData);
        return () => {
            room.off(RoomEvent.DataReceived, onData);
        };
    }, [room, cap]);
    return entries;
}
//# sourceMappingURL=useGraphEvents.js.map